/**
 * ResponsivePreviewFrame — wraps page content with a responsive device frame
 * when preview mode is active. Shows a visual device chrome with size indicator.
 */

import { useContentEditingSafe, type PreviewDevice } from "./ContentEditingContext";
import { useMemo } from "react";

const DEVICE_SPECS: Record<PreviewDevice, { width: string; label: string; maxWidth: number | null }> = {
  desktop: { width: "100%", label: "Desktop", maxWidth: null },
  tablet: { width: "768px", label: "Tablet \u2014 768px", maxWidth: 768 },
  mobile: { width: "375px", label: "Mobile \u2014 375px", maxWidth: 375 },
};

export function ResponsivePreviewFrame({ children }: { children: React.ReactNode }) {
  const ctx = useContentEditingSafe();

  const isActive = ctx?.isPreviewMode && ctx.previewDevice !== "desktop";
  const device = ctx?.previewDevice || "desktop";
  const spec = DEVICE_SPECS[device];

  const frameStyle = useMemo(() => {
    if (!isActive || !spec.maxWidth) return {};
    return {
      maxWidth: spec.maxWidth,
      width: "100%",
    };
  }, [isActive, spec.maxWidth]);

  if (!isActive) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-200 flex flex-col items-center py-6 px-4">
      {/* Device info label */}
      <div className="mb-3 flex items-center gap-2 text-xs text-gray-500 select-none">
        <span
          className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"
        />
        <span style={{ fontWeight: 600 }}>{spec.label}</span>
        <span className="text-gray-400">{"\u2014"}</span>
        <span className="text-gray-400">{"\u05EA\u05E6\u05D5\u05D2\u05D4 \u05DE\u05E7\u05D3\u05D9\u05DE\u05D4"}</span>
      </div>

      {/* Device frame */}
      <div
        className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-300 relative"
        style={frameStyle}
      >
        {/* Top notch (mobile only) */}
        {device === "mobile" && (
          <div className="h-6 bg-gray-900 flex items-center justify-center relative">
            <div className="w-20 h-4 bg-gray-800 rounded-b-xl" />
          </div>
        )}

        {/* Tablet top bar */}
        {device === "tablet" && (
          <div className="h-3 bg-gray-100 border-b border-gray-200 flex items-center justify-center">
            <div className="w-16 h-1 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Content area */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 140px)" }}>
          {children}
        </div>

        {/* Bottom bar (mobile) */}
        {device === "mobile" && (
          <div className="h-5 bg-gray-900 flex items-center justify-center">
            <div className="w-24 h-1 bg-gray-600 rounded-full" />
          </div>
        )}
      </div>
    </div>
  );
}
